import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { Router, Route, Switch, Redirect } from "react-router-dom";
import "react-app-polyfill/stable";

import "bootstrap/dist/css/bootstrap.css";
import "assets/css/now-ui-dashboard.css?v1.4.0";
import "assets/css/demo.css";
import "assets/scss/generalStyles.scss";
import "assets/scss/SolicitudVacaciones.scss";
import "assets/scss/stylesTableReact.scss";
import "assets/scss/datosPersonales.scss";
import "assets/scss/tables.scss";
import "react-metismenu/dist/react-metismenu-standart.min.css";

import WebLayout from "layouts/Web.js";
import AdminLayout from "layouts/Admin.js";
import DirectivoLayout from "layouts/DirectivoLayout";
import ConsultorLayout from "layouts/ConsultorLayout";
import DoctorLayout from "layouts/DoctorLayout";
import LoginLayout from "layouts/Login.js";

import { history } from "./helpers/history";
import { authenticationService } from "./services/authentication.service";
import { PrivateRoute } from "./components/Varios/PrivateRoute";

import { AnunciosPrincipal } from "./components/AnunciosPrincipal";
import { VerAnuncio } from "./components/VerAnuncio";

import { DatosGenerales } from "./components/DatosGenerales";
import Inicio from "components/NoLogin/Inicio";
import Cargando from "components/Varios/Cargando";
import SideBarContextProvider from "Context/SideBarContext";
import NoLoginLayout from "layouts/NoLogin";
import Formularios from "components/NoLogin/Formularios";

import { Worker } from "@react-pdf-viewer/core";
import ScrollTop from "helpers/ScrollTop";

import { MsalProvider, useMsal } from "@azure/msal-react";
import { msalInstance } from "helpers/EntraID/msalInstance";
import { loginRequest } from "helpers/EntraID/authConfig";
import { InteractionStatus } from "@azure/msal-browser";

type UserData = {
    usuario: string;
    idpersona: number;
    identificacion: string;
    apellidos: string;
    nombres: string;
    ultimoCambio: string;
    roles: string[];
    token: string;
    roles_usuario: string[];
};

const currentUser_index: UserData = {
    usuario: "",
    idpersona: 0,
    identificacion: "",
    apellidos: "",
    nombres: "",
    ultimoCambio: "",
    roles: [],
    token: "",
    roles_usuario: [],
};

const AppContent: React.FC = () => {
    const { instance, inProgress, accounts } = useMsal();
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState<UserData>(currentUser_index);

    const saveRedirectUrl = useCallback(() => {
        const currentPath = window.location.href;
        if (
            !currentPath.includes("/login") &&
            !currentPath.includes("/signin-redirect")
        ) {
            sessionStorage.setItem("redirectUrl", currentPath);
        }
    }, []);

    const getAndClearRedirectUrl = useCallback(() => {
        const redirectUrl = sessionStorage.getItem("redirectUrl");
        sessionStorage.removeItem("redirectUrl");
        return redirectUrl || "/web";
    }, []);

    const redirectToLogin = useCallback(async () => {
        try {
            if (inProgress !== "none") {
                console.log("Login ya en curso...");
                return;
            }
            saveRedirectUrl();
            await instance.loginRedirect(loginRequest);
        } catch (error: any) {
            console.error("Error en login redirect:", error);
        }
    }, [inProgress, instance, saveRedirectUrl]);

    const checkExistingAuth = useCallback(async () => {
        // if (accounts.length > 0) {
        //     const account = accounts[0];
        //     instance.setActiveAccount(account);

        //     const username = account.username.split("@")[0];
        //     const userData: any = await authenticationService.login(username);

        //     setCurrentUser(userData);
        //     setLoading(false);
        //     return true;
        // }

        if (accounts.length === 1) {
            const account = accounts[0];
            instance.setActiveAccount(account);

            const username = account.username.split("@")[0];
            const userData: any = await authenticationService.login(username);

            setCurrentUser(userData);
            setLoading(false);
            return true;
        }
        else if (accounts.length > 1) {
            // Forzar a que aparezca el popup para elegir usuario
            const loginResponse = await instance.loginPopup(loginRequest);

            // Ahora tomas el account que eligió el usuario
            const account = loginResponse.account!;
            instance.setActiveAccount(account);

            const username = account.username.split("@")[0];
            const userData: any = await authenticationService.login(username);

            setCurrentUser(userData);
            setLoading(false);
            return true;
        }
        return false;
    }, [accounts, instance]);

    useEffect(() => {
        const handleAuth = async () => {
            const currentPath = window.location.pathname;
            const isRedirect =
                currentPath === "/" || currentPath.includes("/signin-redirect");

            if (
                currentPath !== "/" &&
                !currentPath.includes("/login") &&
                !currentPath.includes("/signin-redirect")
            ) {
                sessionStorage.setItem("redirectUrl", currentPath);
            }

            const hasAuth = await checkExistingAuth();
            if (hasAuth) {
                setLoading(false);
                return;
            }

            if (isRedirect && !currentPath.includes("/formularios/")) {
                try {
                    const response = await instance.handleRedirectPromise();
                    if (response && response.account) {
                        instance.setActiveAccount(response.account);

                        const username = response.account.username.split("@")[0];
                        const userData: any = await authenticationService.login(username);
                        setCurrentUser(userData);

                        setLoading(false);
                        return;
                    }
                } catch (err) {
                    console.error("Error en redirect de MSAL:", err);
                }
            }

            setLoading(false);
        };

        handleAuth();
    }, [instance, checkExistingAuth]);

    const controlar_usuario = (
        componente: JSX.Element,
        datosUsuario: UserData
    ) => {
        if (!datosUsuario || !datosUsuario.token) {
            if (inProgress !== "none") {
                return <Cargando mensaje="Procesando autenticación..." />;
            }
            redirectToLogin();
            return <Cargando mensaje="Redirigiendo al login..." />;
        }
        return componente;
    };

    if (loading) return <Cargando />;

    return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.6.347/build/pdf.worker.min.js">
            <SideBarContextProvider>
                <Router history={history}>
                    <ScrollTop />
                    <Switch>
                        <Route exact path="/" render={(props) => <NoLoginLayout {...props} />} />
                        <Route exact path="/formularios" render={(props) => <NoLoginLayout {...props} />} />

                        <Route
                            exact
                            path="/signin-redirect"
                            render={() => {
                                const redirectUrl = getAndClearRedirectUrl();
                                const rutas = window.location.host;
                                const relativeruta = redirectUrl.split(rutas)[1];
                                return <Redirect to={relativeruta} />;
                            }}
                        />
                        <Route
                            path="/web"
                            render={(props) =>
                                controlar_usuario(<WebLayout {...props} />, currentUser)
                            }
                        />
                        <Route
                            path="/directivo"
                            render={(props) =>
                                controlar_usuario(<DirectivoLayout {...props} />, currentUser)
                            }
                        />
                        <Route
                            path="/doctor"
                            render={(props) =>
                                controlar_usuario(<DoctorLayout {...props} />, currentUser)
                            }
                        />
                        <Route
                            path="/consultor"
                            render={(props) =>
                                controlar_usuario(<ConsultorLayout {...props} />, currentUser)
                            }
                        />
                        <Route
                            path="/admin"
                            render={(props) =>
                                controlar_usuario(<AdminLayout {...props} />, currentUser)
                            }
                        />
                    </Switch>
                </Router>
            </SideBarContextProvider>
        </Worker>
    );
};

const App: React.FC = () => (
    <MsalProvider instance={msalInstance}>
        <AppContent />
    </MsalProvider>
);

ReactDOM.render(<App />, document.getElementById("root"));
